import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, ButtonGroup, Col, Row } from 'reactstrap';

import Analytics from '../../../analytics/Analytics';
import globalTranslations from '../../../i18n/globalTranslations';
import { createForm } from '../../../models/Form';
import { createFormField } from '../../../models/FormField';
import ProductImageModel from '../../../models/ProductImage';
import ProductListProduct from '../../../models/ProductListProduct';
import { stripWishlistItemMeta } from '../../../models/WishlistProduct';
import { modelOf } from '../../../prop-types';
import AccountStore from '../../../store/AccountStore';
import CartStore from '../../../store/CartStore';
import ConfigStore from '../../../store/ConfigStore';
import UIStore from '../../../store/UIStore';
import ProductClass from '../../../types/ProductClass';
import ProductRecurringOrderType from '../../../types/ProductRecurringOrderType';
import { consoleErrorUnlessFiltered } from '../../../util/sentryFilters';
import { productCountValid, quantityValid } from '../../../util/formValidators';
import {
  calculateNearestProductQuantityWithQuantityStep,
  calculateProductPackageSizeWithQuantity,
  roundWithPrecision,
} from '../../../util/number';
import MaybeExternalLink from '../../common/MaybeExternalLink';
import QuantityFieldGroup from '../../common/QuantityFieldGroup';
import FieldError from '../../form/FieldError';
import Price from '../../product/Price';
import ProductAvailabilityText from '../../product/ProductAvailabilityText';
import ProductImage from '../../product/ProductImage';
import ProductPagePrice from '../../product/ProductPagePrice';
import ProductPrice from '../../product/ProductPrice';

const CUSTOM_ASPECT_RATIO = '120';
const QUANTITY_UPDATE_DEBOUNCE_MS = 1200;

@observer
class WishListProductRow extends Component {
  constructor(props) {
    super(props);

    this.state = {
      totalPrice: 0,
      product: this.props.product,
    };

    this.form = null;
    this.maxQuantity = 99999;
    this.defaultValue = 0;
    this.accessToken = null;
    this.quantityField = null;
    this.debouncedPersistQuantity = debounce((nextProduct) => {
      this.props.onStorageUpdate(nextProduct);
    }, QUANTITY_UPDATE_DEBOUNCE_MS);

    this.defaultValue = calculateProductPackageSizeWithQuantity(
      this.getQuantityStep(this.state.product),
      this.state.product.quantity
    );

    const formField = this.createQuantityFormFields(this.props.product);
    this.initializeQuantityFormField(formField);
  }

  componentDidMount() {
    this.calculateTotalPrice();
  }

  componentWillUnmount() {
    this.debouncedPersistQuantity.cancel();
  }

  componentDidUpdate(prevProps) {
    const prevProductId = this.getProductIdByProductClass(prevProps.product);
    const nextProductId = this.getProductIdByProductClass(this.props.product);

    if (prevProductId !== nextProductId) {
      this.debouncedPersistQuantity.cancel();
      const formField = this.createQuantityFormFields(this.props.product);
      this.initializeQuantityFormField(formField);
      this.setState({ product: this.props.product }, () => {
        this.calculateTotalPrice();
      });
      return;
    }

    if (prevProps.product !== this.props.product) {
      this.setState({ product: this.props.product }, () => {
        this.calculateTotalPrice();
      });
    }
  }

  hasAccessToken = () => {
    const { match } = this.props;
    return !!(
      match.params.hasOwnProperty('accessToken') && match.params.accessToken
    );
  };

  createQuantityFormFields = (product) => {
    let formFields = {};
    formFields[this.getQuantityFieldName(product)] = createFormField(
      {
        value: String(product.quantity),
      },
      [
        quantityValid(this.maxQuantity),
        productCountValid(this.getQuantityStep(product)),
      ]
    );
    return formFields;
  };

  getQuantityFieldName = (product = this.state.product) =>
    'quantity' + product.product_id;

  initializeQuantityFormField = (formFields) => {
    this.form = null;
    this.form = createForm(formFields);
  };

  renderProductImageOnLargeDisplays = (product) => {
    const element = (
      <ProductImage
        product={product}
        productImage={this.createProductImage(product)}
        size="small"
        forceAspectRatio={true}
        overrideConfigStoreAspectRatio={true}
        customAspectRatio={CUSTOM_ASPECT_RATIO}
        lazyLoading={false}
      />
    );
    return product.link ? this.renderElementWithLinkIfExists(element) : element;
  };

  renderProductName = (product) => {
    const element = product.actual_code + ' - ' + product.name;
    return product.link ? this.renderElementWithLinkIfExists(element) : element;
  };

  renderElementWithLinkIfExists = (element) => (
    <MaybeExternalLink to={this.state.product.link}>
      {element}
    </MaybeExternalLink>
  );

  renderProductAvailabilityText = (product) => (
    <div>
      <ProductAvailabilityText availabilityHtml={product.availability_html} />
    </div>
  );

  renderProductVariations = (product) => {
    return (
      <ul className="WishListProductRow__product-variations">
        {product.variations.map((variation, i) => {
          if (!variation) {
            return null;
          }

          return (
            <li key={i} className="WishListProductRow__product-variation">
              {variation}
            </li>
          );
        })}
      </ul>
    );
  };

  createProductImage = (product) => {
    const url = product.image;
    return url
      ? ProductImageModel.create({
          id: 0,
          product_id: this.getProductIdByProductClass(product),
          sizes: {
            small: url,
          },
        })
      : null;
  };

  getProductIdByProductClass = (product) => {
    return product.class === ProductClass.MULTI ||
      product.class === ProductClass.COLLECTION
      ? product.id
      : product.product_id;
  };

  renderAddToCartQuantity = () => {
    const { disableActions } = this.props;

    if (!this.state.product.price_info) {
      return null;
    }

    this.quantityField = this.form.fields.get(this.getQuantityFieldName());
    return (
      <QuantityFieldGroup
        key={this.state.product.product_id}
        disabled={!!this.ifQuantityFieldDisabled() || disableActions}
        className={
          'WishListProductRow__product-list-quantity-group ' +
          this.state.product.product_id
        }
        field={this.quantityField}
        quantityStep={this.getQuantityStep(this.state.product)}
        defaultValue={this.defaultValue}
        maxQuantity={this.maxQuantity}
        unit={this.state.product.stock_unit}
        updateOnBlur={true}
        onPreviewProductQuantity={this.updateLocalQuantityPreview.bind(this)}
        onUpdateProductQuantity={this.updateProductQuantityDebounced.bind(this)}
        onBlurProductQuantity={this.updateProductQuantityImmediate.bind(this)}
      />
    );
  };

  ifQuantityFieldDisabled = () => {
    return this.hasAccessToken();
  };

  getQuantityStep = (product) => {
    return product && product.package_size > 1 ? product.package_size : 1;
  };

  showDiscountPriceIfExists = () => {
    const { product } = this.state;

    if (!product || !product.price_info) {
      return null;
    }

    const productListProduct = ProductListProduct.create(
      stripWishlistItemMeta(product)
    );
    const price = (
      <ProductPrice
        className={'WishlistProductRow__product-row-product-page-price'}
        priceInfo={productListProduct.price_info}
      />
    );
    return (
      <ProductPagePrice
        className={'WishlistProductRow__product-row-product-page-price'}
        price={price}
        product={productListProduct}
      />
    );
  };

  renderTotalPriceAndQuantity = (product) => {
    return (
      <div className="WishListProductRow__product-list-price-and-quantity">
        {this.renderAddToCartQuantity(product)}
        {this.showTotalPriceIfExists(product)}
        <FieldError field={this.form.fields.get(this.getQuantityFieldName())} />
      </div>
    );
  };

  showTotalPriceIfExists = () => {
    const { product, totalPrice } = this.state;
    if (
      !product ||
      !product.price_info ||
      !this.quantityField.value ||
      this.quantityField.value === '0'
    ) {
      return null;
    }

    return (
      <Price
        className="WishListProductRow__product-row-total-price"
        price={totalPrice}
      />
    );
  };

  renderWishListProductRowButtons = (product) => {
    const { accountStore, configStore, disableActions } = this.props;
    const isSharedWishlist = this.hasAccessToken();
    const buyingAllowed = !(
      configStore.recurringOrder.enabled &&
      accountStore.loggedIn &&
      product.recurring_order_type ===
        ProductRecurringOrderType.RECURRING_ORDERS_ONLY
    );

    return (
      <div className="WishListProductRow__product-list-buttons">
        <ButtonGroup vertical>
          {buyingAllowed && (
            <Button
              color="primary"
              disabled={this.validateAddToCartButton() || disableActions}
              onClick={this.productAddToCart.bind(this, product)}
            >
              <FormattedMessage {...globalTranslations.addToCartButton} />
            </Button>
          )}
          <Button
            disabled={isSharedWishlist || disableActions}
            color="secondary"
            onClick={this.deleteProduct.bind(this, product)}
          >
            <FormattedMessage
              id="wishlist.DeleteProduct"
              defaultMessage="Delete"
            />
          </Button>
        </ButtonGroup>
      </div>
    );
  };

  validateAddToCartButton = () => {
    const quantity = this.getCurrentAddToCartQuantity();

    return (
      this.form.valid === false ||
      !this.state.product.price_info ||
      !quantity ||
      quantity <= 0
    );
  };

  getCurrentAddToCartQuantity = () => {
    const field =
      this.quantityField || this.form.fields.get(this.getQuantityFieldName());

    if (!field || field.value === '') {
      return null;
    }

    const quantity = calculateNearestProductQuantityWithQuantityStep(
      Number(field.value),
      this.getQuantityStep(this.state.product)
    );

    return Number.isFinite(quantity) ? quantity : null;
  };

  updateProductQuantity = (packageQuantity, { immediate = false } = {}) => {
    this.form.validateField(this.getQuantityFieldName());

    if (this.form.valid === false || packageQuantity <= 0) {
      this.debouncedPersistQuantity.cancel();
      return;
    }

    const { product } = this.props;
    const quantity = calculateNearestProductQuantityWithQuantityStep(
      packageQuantity,
      this.getQuantityStep(product)
    );
    this.updateLocalPackageQuantity(quantity, packageQuantity, { immediate });
  };

  updateProductQuantityDebounced = (packageQuantity) => {
    this.updateProductQuantity(packageQuantity, { immediate: false });
  };

  updateProductQuantityImmediate = (packageQuantity) => {
    this.updateProductQuantity(packageQuantity, { immediate: true });
  };

  updateLocalQuantityPreview = (packageQuantity) => {
    this.form.validateField(this.getQuantityFieldName());

    if (packageQuantity <= 0 || Number.isNaN(packageQuantity)) {
      this.setState((prevState) => ({
        product: {
          ...prevState.product,
          quantity: 0,
        },
        totalPrice: 0,
      }));
      return;
    }

    const { product } = this.props;
    const quantity = calculateNearestProductQuantityWithQuantityStep(
      packageQuantity,
      this.getQuantityStep(product)
    );

    const nextProduct = {
      ...this.state.product,
      quantity: Math.ceil(quantity),
    };

    this.setState({ product: nextProduct }, () => {
      this.calculateTotalPrice();
    });
    this.defaultValue = packageQuantity;
  };

  calculateTotalPrice = () => {
    const { product } = this.state;

    if (!product || !product.price_info) {
      return null;
    }

    this.setState({
      totalPrice: product.quantity * product.price_info.price.with_tax,
    });

    if (product.package_size) {
      this.setState({
        totalPrice:
          product.quantity *
          product.package_size *
          product.price_info.price.with_tax,
      });
    }
  };

  updateLocalPackageQuantity = (
    quantity,
    packageQuantity,
    { immediate } = {}
  ) => {
    const product = {
      ...this.state.product,
      quantity: Math.ceil(quantity),
    };
    this.setState({ product }, () => {
      if (immediate) {
        this.debouncedPersistQuantity.cancel();
        this.props.onStorageUpdate(this.state.product);
      } else {
        this.debouncedPersistQuantity(this.state.product);
      }
      this.calculateTotalPrice();
    });
    this.defaultValue = packageQuantity;
  };

  productAddToCart = () => {
    const { analytics, accountStore, cartStore } = this.props;
    const { product } = this.state;
    const withTax = accountStore.showPricesWithTax;
    const wishlistProduct = ProductListProduct.create(
      stripWishlistItemMeta(product)
    );
    const activeProductId = wishlistProduct.id;

    this.form.validate();
    const quantity = this.getCurrentAddToCartQuantity();

    if (this.form.valid && quantity > 0) {
      cartStore
        .addToCart(activeProductId, quantity, [], [], wishlistProduct)
        .then(() => {
          const analyticsProduct = {
            product: wishlistProduct,
            quantity,
            activeProductId,
          };
          const totalValue = wishlistProduct.getAnalyticsValues(withTax).price;

          analytics.addToCart(
            [analyticsProduct],
            roundWithPrecision(totalValue, 4)
          );
          analytics.cartStatus({
            cart: cartStore.cart,
          });
        })
        .catch((err) => consoleErrorUnlessFiltered(err));
    }
  };

  deleteProduct = (product) => {
    this.debouncedPersistQuantity.cancel();
    this.props.onProductDelete(product);
  };

  render() {
    const { uiStore, product } = this.props;
    const isSharedWishlist = this.hasAccessToken();

    return (
      <Row
        className={classNames(
          'WishListProductRow',
          'WishListProductRow__product-list-row',
          {
            'WishListProductRow--actions-disabled': this.props.disableActions,
            'WishListProductRow--read-only': isSharedWishlist,
          }
        )}
      >
        {uiStore.isDesktop && (
          <Col lg={2}>
            <div className="WishListProductRow__product-image">
              {this.renderProductImageOnLargeDisplays(product)}
            </div>
          </Col>
        )}
        <Col xs={12} lg={2} className="WishListProductRow__product-list-col">
          <div className="WishListProductRow__product-list-product">
            <div className="WishListProductRow__product-name">
              {this.renderProductName(product)}
              {this.renderProductAvailabilityText(product)}
              {product.variations?.length > 0 &&
                this.renderProductVariations(product)}
              {(uiStore.isMobile || uiStore.isTablet) &&
                this.showDiscountPriceIfExists(product.price_info)}
              {(uiStore.isMobile || uiStore.isTablet) &&
                this.renderTotalPriceAndQuantity(product)}
            </div>
          </div>
        </Col>
        {uiStore.isDesktop && (
          <Col md={2} className="WishListProductRow__product-list-col">
            {this.showDiscountPriceIfExists(product.price_info)}
          </Col>
        )}
        {uiStore.isDesktop && (
          <Col md={4} className="WishListProductRow__product-list-col">
            {this.renderTotalPriceAndQuantity(product)}
          </Col>
        )}
        <Col xs={12} lg={2} className="WishListProductRow__product-list-col">
          {this.renderWishListProductRowButtons(product)}
        </Col>
      </Row>
    );
  }
}

WishListProductRow.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  cartStore: modelOf(CartStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  analytics: PropTypes.instanceOf(Analytics).isRequired,
  match: PropTypes.object.isRequired,
  onProductDelete: PropTypes.func.isRequired,
  onStorageUpdate: PropTypes.func.isRequired,
  product: PropTypes.object.isRequired,
  disableActions: PropTypes.bool,
};

export default inject(
  'accountStore',
  'cartStore',
  'configStore',
  'uiStore',
  'analytics'
)(WishListProductRow);
